import React, { useCallback, useEffect, useState } from "react";
import { cls } from "cls";
import { PlusLink } from "@clarkinc/zest-design-system/ui";
import { connect } from "react-redux";
// eslint-disable-next-line import/no-unresolved
import { RootState } from "StoreTypes";
import formatAsCurrency from "@wss/utilities/formatAsCurrency";
import { User, SamplePurchaseHistory } from "~/common/Store/models/User";
import AddToCart, {
  SamplingAddToCartProduct,
} from "./Sampling/SamplingAddToCart";
import Icon from "~/common/Icons/Standard";

interface Props {
  maxQuantityPerOrder?: number;
  maxQuantityPerCustomer?: number;
  mounted: boolean;
  labelType?: string;
  product: SamplingAddToCartProduct;
  user: User;
}

const ProductDetailSampling = ({
  maxQuantityPerOrder = 0,
  maxQuantityPerCustomer = 0,
  labelType,
  product,
  user,
}: Props) => {
  const [showSampleProduct, setShowSampleProduct] = useState(false);

  const {
    description,
    plusPrice,
    price,
    isWebstaurantPlusEligible,
    itemNumberId,
  } = product;

  const isConsumable = labelType?.toLocaleLowerCase() === "consumables";

  const samplePreviousPurchase = useCallback(
    (itemNumberId: number) => {
      const sampleItem = user.samplePurchaseHistory?.find(
        (x) => Number(x.itemNumberId) === itemNumberId,
      );
      return sampleItem || null;
    },
    [user.samplePurchaseHistory],
  );

  useEffect(() => {
    const sampleHistory = samplePreviousPurchase(
      itemNumberId,
    ) as unknown as SamplePurchaseHistory;

    if (
      !user.registered ||
      (user.registered &&
        (!isConsumable || (user.isCommercial && isConsumable)) &&
        (sampleHistory === null ||
          sampleHistory.totalQuantity < maxQuantityPerCustomer))
    ) {
      setShowSampleProduct(true);
    } else {
      setShowSampleProduct(false);
    }
  }, [
    user.registered,
    user.isCommercial,
    samplePreviousPurchase,
    itemNumberId,
    maxQuantityPerCustomer,
    user,
    isConsumable,
  ]);

  return showSampleProduct ? (
    <div
      className={cls(
        "box",
        "padded",
        "min-h-[80px]",
        "flex",
        "justify-between",
      )}
      data-testid="pdp-sampling"
    >
      <div className={cls("flex")}>
        <Icon name="sample" className={cls("w-10", "h-10")} />
      </div>
      <div className={cls("flex", "flex-col", "px-2")}>
        <p className={cls("text-base", "font-bold", "text-gray-800", "mb-0")}>
          Try a Sample
        </p>
        <p
          className={cls(
            "text-sm",
            "lt:text-xs",
            "leading-tight",
            "text-gray-800",
            "mb-0",
          )}
        >
          {description}
        </p>{" "}
        <p
          className={cls(
            "text-gray-800",
            "text-[10px]",
            "leading-3",
            "font-semibold",
            "mb-0",
            "pb-2",
          )}
          id="max-quantity-per-customer"
        >
          {`Max ${maxQuantityPerOrder} per customer${
            isConsumable ? " and ships free" : ""
          }`}
        </p>
        <AddToCart product={product} user={user} isConsumable={isConsumable} />
      </div>
      <div
        className={cls(
          "flex",
          "flex-col",
          "flex-grow",
          "w-9/12",
          "xsl:w-auto",
          "lt:w-9/12",
        )}
      >
        <div className={cls("w-min", "ml-auto")}>
          <div
            className={cls(
              "border",
              "border-solid",
              "border-gray-300",
              "rounded",
              "flex",
              "flex-col",
              "justify-center",
              "items-center",
              "py-2",
              "px-4",
              "lt:px-2",
              "xl:px-4",
            )}
          >
            <p
              className={cls(
                "mb-0",
                "text-xs",
                "font-semibold",
                "leading-none",
                "text-gray-900",
              )}
            >
              Sample
            </p>
            {isConsumable ? (
              <p
                className={cls(
                  "text-2xl",
                  "font-bold",
                  "leading-none",
                  "text-red-500",
                  "mb-0",
                )}
              >
                FREE
              </p>
            ) : (
              <p
                className={cls(
                  "text-2xl",
                  "font-bold",
                  "leading-none",
                  "text-red-500",
                  "pt-1",
                  "mb-0",
                )}
              >
                {formatAsCurrency.formatIfUndefined(price)}
              </p>
            )}
          </div>
          {!isConsumable && isWebstaurantPlusEligible && plusPrice > 0 && (
            <div
              data-component="Pricing"
              className={cls(
                "flex",
                "justify-center",
                "items-center",
                "pt-1.5",
              )}
            >
              <PlusLink
                className="inline-flex"
                iconProps={{
                  "data-testid": "plus-badge-icon-new",
                }}
              />
              <span
                className={cls(
                  "text-base",
                  "pl-0.5",
                  "font-bold",
                  "text-blue-900",
                  "leading-none",
                )}
              >
                {formatAsCurrency.formatIfUndefined(plusPrice)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  ) : (
    <></>
  );
};

export const mapStateToProps = (state: RootState, ownProps: Props) => {
  if (!ownProps.mounted) return { user: ownProps.user };

  return { user: state.user };
};

export default connect(mapStateToProps)(ProductDetailSampling);
